import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicCli from '../../main/components/basicCli/BasicCli';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import { formatDateByTimeZone, getCompanyTimeZone } from '../../main/utils/timezone';
import useGetUser from '../../main/hooks/useGetUser';
import './InvoiceOwnerIssues.css';

interface HistoryRow {
  id: number; action: string; actionAt: string; actionByName?: string;
  ownerBefore?: number | null; ownerBeforeName?: string;
  ownerAfter?: number | null; ownerAfterName?: string; changesJson?: string | null;
}
interface OwnerIssue {
  invoiceId: number; tableCode?: string; created: string;
  currentOwnerId: number; currentOwnerName?: string;
  originalOwnerId?: number | null; originalOwnerName?: string;
  ownershipChangeCount: number; history: HistoryRow[];
}

const InvoiceOwnerIssues: React.FC = () => {
  const { t } = useTranslation();
  const user = useGetUser();
  const timeZone = getCompanyTimeZone(user);
  const [items, setItems] = useState<OwnerIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const response = await axiosInstance.get(ApiConstants.invoice.ownerIssues);
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || t('invoice.ownerIssuesLoadError', { defaultValue: 'Unable to load invoice owner issues.' }));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { void load(); }, [load]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter(x => [x.invoiceId, x.tableCode, x.currentOwnerName, x.originalOwnerName]
      .some(value => String(value ?? '').toLowerCase().includes(term)));
  }, [items, search]);
  const owner = (name?: string, id?: number | null) => name || (id ? `#${id}` : '-');

  return <BasicCli><div className="ioiPage bcPageContent">
    <div className="ioiHeader">
      <div><h1 className="bcPageTitle">{t('invoice.ownerIssuesTitle', { defaultValue: 'Invoice Owner Issue' })}</h1>
        <p>{t('invoice.ownerIssuesSubtitle', { defaultValue: 'Invoices whose recorded ownership differs from their current owner.' })}</p></div>
      <button className="ioiRefresh" onClick={() => void load()} disabled={loading}>{t('generic.refresh', { defaultValue: 'Refresh' })}</button>
    </div>
    <input className="ioiSearch" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('generic.search', { defaultValue: 'Search' })} />
    {error && <div className="ioiError">{error}</div>}
    <div className="ioiTableWrap"><table className="ioiTable"><thead><tr>
      <th>Invoice</th><th>{t('generic.table', { defaultValue: 'Table' })}</th><th>{t('generic.date', { defaultValue: 'Created' })}</th>
      <th>{t('invoice.originalOwner', { defaultValue: 'Original owner' })}</th><th>{t('invoice.currentOwner', { defaultValue: 'Current owner' })}</th>
      <th>{t('invoice.ownerChanges', { defaultValue: 'Issues' })}</th><th></th>
    </tr></thead><tbody>
      {loading ? <tr><td colSpan={7} className="ioiEmpty">Loading...</td></tr> : filtered.length === 0 ?
        <tr><td colSpan={7} className="ioiEmpty">{t('invoice.ownerIssuesEmpty', { defaultValue: 'No invoice owner issues found.' })}</td></tr> :
        filtered.map(item => <React.Fragment key={item.invoiceId}><tr className="ioiIssueRow">
          <td>#{item.invoiceId}</td><td>{item.tableCode || '-'}</td><td>{formatDateByTimeZone(item.created, timeZone) || item.created}</td>
          <td>{owner(item.originalOwnerName, item.originalOwnerId)}</td><td>{owner(item.currentOwnerName, item.currentOwnerId)}</td>
          <td><span className="ioiBadge">{item.ownershipChangeCount}</span></td>
          <td><button className="ioiDetails" onClick={() => setExpanded(expanded === item.invoiceId ? null : item.invoiceId)}>{expanded === item.invoiceId ? 'Hide' : 'History'}</button></td>
        </tr>{expanded === item.invoiceId && <tr><td colSpan={7} className="ioiHistoryCell"><table className="ioiHistory"><thead><tr>
          <th>Date</th><th>Action</th><th>Performed by</th><th>Owner before</th><th>Owner after</th><th>Details</th>
        </tr></thead><tbody>{item.history.map(h => <tr key={h.id}><td>{formatDateByTimeZone(h.actionAt, timeZone) || h.actionAt}</td><td>{h.action}</td>
          <td>{h.actionByName || '-'}</td><td>{owner(h.ownerBeforeName, h.ownerBefore)}</td><td>{owner(h.ownerAfterName, h.ownerAfter)}</td><td>{h.changesJson || '-'}</td></tr>)}</tbody></table></td></tr>}
        </React.Fragment>) }
    </tbody></table></div>
  </div></BasicCli>;
};

export default InvoiceOwnerIssues;
